
import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== TOPIC API FUNCTIONS ====================

/**
 * Get list of all topics (Authenticated users)
 * @returns {Promise} Array of topics
 */
export const getAllTopics = async () => {
    try {
        const response = await api.get('/topics/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get topics for a single level (server-side filtered)
 * @param {number} levelId - Level ID to filter by
 * @returns {Promise} Array of topics belonging to the level
 */
export const getTopicsByLevel = async (levelId) => {
    try {
        const response = await api.get(`/topics/?level=${levelId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single topic by id (server-side filtered via the list endpoint -- there's no GET on the
 * topic detail route). Includes `level_name` already embedded, so callers that only need the
 * topic's level name don't need a separate levels fetch.
 * @param {number} topicId - Topic ID to fetch
 * @returns {Promise<Object|null>} The topic, or null if not found
 */
export const getTopicById = async (topicId) => {
    try {
        const response = await api.get(`/topics/?id=${topicId}`);
        return response.data[0] || null;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new topic (Admin only)
 * @param {Object} topicData - Topic data object
 * @param {string} topicData.title - Topic title
 * @param {number} topicData.level - Level ID
 * @param {number} topicData.order - Topic order
 * @returns {Promise} Created topic data
 */
export const createTopic = async (topicData) => {
    try {
        const response = await api.post('/topics/', topicData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing topic (Admin only)
 * @param {number} topicId - Topic ID
 * @param {Object} topicData - Updated topic data
 * @returns {Promise} Updated topic data
 */
export const updateTopic = async (topicId, topicData) => {
    try {
        const response = await api.patch(`/topics/${topicId}/`, topicData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a topic (Admin only)
 * @param {number} topicId - Topic ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteTopic = async (topicId) => {
    try {
        const response = await api.delete(`/topics/${topicId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export default api;


// ==================== TOPIC CONTENT API FUNCTIONS ====================

/**
 * Get list of all topic contents (Authenticated users)
 * @returns {Promise} Array of topic contents
 */
export const getAllTopicContents = async () => {
    try {
        const response = await api.get('/topic-contents/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get topic contents for a single level (server-side filtered, spans every topic in the level)
 * @param {number} levelId - Level ID to filter by
 * @returns {Promise} Array of topic contents belonging to the level
 */
export const getTopicContentsByLevel = async (levelId) => {
    try {
        const response = await api.get(`/topic-contents/?level=${levelId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single topic content by id (server-side filtered via the list endpoint -- there's no GET
 * on the topic content detail route).
 * @param {number} topicContentId - TopicContent ID to fetch
 * @returns {Promise<Object|null>} The topic content, or null if not found
 */
export const getTopicContentById = async (topicContentId) => {
    try {
        const response = await api.get(`/topic-contents/?id=${topicContentId}`);
        return response.data[0] || null;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new topic content (Admin only)
 * @param {Object} topicContentData - Topic content data object
 * @returns {Promise} Created topic content data
 */
export const createTopicContent = async (topicContentData) => {
    try {
        const response = await api.post('/topic-contents/', topicContentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing topic content (Admin only)
 * @param {number} topicContentId - Topic content ID
 * @param {Object} topicContentData - Updated topic content data
 * @returns {Promise} Updated topic content data
 */
export const updateTopicContent = async (topicContentId, topicContentData) => {
    try {
        const response = await api.patch(`/topic-contents/${topicContentId}/`, topicContentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a topic content (Admin only)
 * @param {number} topicContentId - Topic content ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteTopicContent = async (topicContentId) => {
    try {
        const response = await api.delete(`/topic-contents/${topicContentId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};